const PENDING_ERROR_KEY = 'passCheckoutPendingError';

export type PassCheckoutErrorCode = 'connectNotReady' | 'generic';

function getHttpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { status?: number } }).response;
    return response?.status;
  }
  return undefined;
}

function getResponseMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      for (const key of ['message', 'error', 'detail', 'errorMessage'] as const) {
        if (typeof record[key] === 'string') return record[key] as string;
      }
    }
  }
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '';
}

/**
 * Backend rejects pass checkout with 400 when the owning org's Stripe Connect
 * Express account is not ready for charges (connected-account migration).
 */
export function isPassCheckoutConnectNotReadyError(error: unknown): boolean {
  if (getHttpStatus(error) !== 400) {
    return false;
  }
  const message = getResponseMessage(error).toLowerCase();
  return (
    message.includes('stripe connect') &&
    (message.includes('not properly configured') || message.includes('online payments'))
  );
}

export function getPassCheckoutErrorCode(error: unknown): PassCheckoutErrorCode {
  return isPassCheckoutConnectNotReadyError(error) ? 'connectNotReady' : 'generic';
}

/** Stashed when post-login checkout resume fails so the return page can show a message. */
export function setPassCheckoutPendingError(code: PassCheckoutErrorCode): void {
  try {
    sessionStorage.setItem(PENDING_ERROR_KEY, code);
  } catch (err) {
    console.warn('Failed to store pass checkout pending error:', err);
  }
}

export function consumePassCheckoutPendingError(): PassCheckoutErrorCode | null {
  try {
    const raw = sessionStorage.getItem(PENDING_ERROR_KEY);
    sessionStorage.removeItem(PENDING_ERROR_KEY);
    if (raw === 'connectNotReady' || raw === 'generic') {
      return raw;
    }
    return null;
  } catch {
    return null;
  }
}
